import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { normalizeIdentity } from '@yourstore/domain';
import { useLive, useRuntime } from './context';
import { useT } from '../messages';
import type { ClientDetail, WireIdentity } from '../wire';

/**
 * **Pick one of her clients, or add one without leaving the sale.**
 *
 * Built for the Gift cards sheet (the owner: *"a client should be able to gift
 * another client — no free typing; a good time to add a new client when
 * someone is buying for someone else"*), and deliberately its own file so any
 * other sheet that names a person can use it rather than grow a third
 * `<select>` of every client in the book.
 *
 * ## Search, not a list
 *
 * A spa's book is hundreds of names; a `<select>` of all of them is a scroll
 * wheel at the counter. This searches the **mirror** — so it works in a
 * tunnel — by name, by phone *digits* (so "340 555 01" finds `+13405550142`)
 * and by email, and shows the first few matches as big buttons.
 *
 * ## A new client is a draft until the sheet says go
 *
 * "+ New client" asks for a name and a phone and/or an email, and hands the
 * sheet a {@link PickedClient} carrying a **`draft`** and an id minted here
 * (`cl_` + ULID, which `CreateClientBody` honours as the primary key). Nothing
 * is queued yet: an owner who adds somebody and then cancels the sale has not
 * added anybody. When the sale is made, the sheet queues `client.create` with
 * that id **first** and the sale after it, and the outbox's total order
 * (§8.2) lands them in that order whether there is signal or not — which is
 * what lets the sale name a client the server has not heard of yet.
 *
 * A phone or email that already belongs to somebody in the book is caught
 * here, with a button to use her instead: the server would refuse the second
 * claim on the identity, and a refused new client would take the sale down
 * with it.
 */

/** A client as a sheet holds one: picked from the book, or typed in and not yet queued. */
export interface PickedClient {
  clientId: string;
  name: string;
  /** A phone or an email to show under the name, when there is one. */
  detail?: string | undefined;
  /** Present until the sheet has queued `client.create` for it. */
  draft?: { name: string; identities: WireIdentity[] } | undefined;
}

/** How many matches are drawn. A search that needs more is a search to narrow. */
const SHOWN = 6;

function digitsOf(text: string): string {
  return text.replace(/\D/g, '');
}

function firstDetail(client: ClientDetail): string | undefined {
  return client.identities[0]?.identity;
}

export function pickedFrom(client: ClientDetail): PickedClient {
  return { clientId: client.client_id, name: client.display_name, detail: firstDetail(client) };
}

/** Name, phone digits (three or more) or email — case-insensitive, by substring. */
export function clientMatches(client: ClientDetail, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') return false;
  if (client.display_name.toLowerCase().includes(needle)) return true;
  const digits = digitsOf(needle);
  return client.identities.some((i) =>
    i.identity_kind === 'phone'
      ? digits.length >= 3 && digitsOf(i.identity).includes(digits)
      : i.identity.toLowerCase().includes(needle),
  );
}

export function ClientPicker({
  label,
  value,
  onChange,
  noneNote,
  initialSearch = '',
}: {
  /** The field's name — "Recipient", "Purchaser". Also the group's accessible name. */
  label: string;
  value: PickedClient | null;
  onChange: (value: PickedClient | null) => void;
  /** What "nobody chosen" means, when it is allowed: *Walk-in / not a client*. */
  noneNote?: string | undefined;
  /** A name to start the search with — a gift request's "for Her sister". */
  initialSearch?: string | undefined;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const clients = useLive(() => runtime.mirror.clients(), []);
  const [search, setSearch] = useState(initialSearch);
  const [adding, setAdding] = useState(false);

  const matches = useMemo(
    () => clients.filter((c) => !c.archived && clientMatches(c, search)).slice(0, SHOWN),
    [clients, search],
  );

  if (value !== null) {
    return (
      <fieldset className="field client-picker">
        <legend>{label}</legend>
        <div className="client-picker__chosen">
          <span className="client-picker__who">
            <strong>{value.name}</strong>
            {value.detail === undefined ? null : <small>{value.detail}</small>}
            {value.draft === undefined ? null : (
              <small className="client-picker__new-badge">{t('clientPicker.isNew')}</small>
            )}
          </span>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              setSearch('');
              onChange(null);
            }}
          >
            {t('clientPicker.change')}
          </button>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset className="field client-picker">
      <legend>{label}</legend>
      <input
        type="search"
        aria-label={label}
        placeholder={t('clientPicker.search')}
        autoComplete="off"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {noneNote !== undefined && search.trim() === '' && !adding ? (
        <small className="muted">{noneNote}</small>
      ) : null}
      {matches.length === 0 ? null : (
        <ul className="client-picker__results">
          {matches.map((client) => (
            <li key={client.client_id}>
              <button
                type="button"
                className="client-picker__option"
                onClick={() => onChange(pickedFrom(client))}
              >
                <strong>{client.display_name}</strong>
                {firstDetail(client) === undefined ? null : <small>{firstDetail(client)}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {search.trim() !== '' && matches.length === 0 && !adding ? (
        <small className="muted">{t('clientPicker.noMatch', { search: search.trim() })}</small>
      ) : null}
      {adding ? (
        <NewClientForm
          seed={search}
          clients={clients}
          onCancel={() => setAdding(false)}
          onPick={(picked) => {
            setAdding(false);
            onChange(picked);
          }}
        />
      ) : (
        <button
          type="button"
          className="button button--ghost client-picker__add"
          onClick={() => setAdding(true)}
        >
          {t('clientPicker.new')}
        </button>
      )}
    </fieldset>
  );
}

/**
 * Name, phone, email — the Clients tab's new-client sheet, inline, plus the
 * email and the "is she already in the book?" check a sale needs.
 */
function NewClientForm({
  seed,
  clients,
  onCancel,
  onPick,
}: {
  /** What was typed in the search: a name lands in Name, digits in Phone, an address in Email. */
  seed: string;
  clients: readonly ClientDetail[];
  onCancel: () => void;
  onPick: (picked: PickedClient) => void;
}): ReactNode {
  const t = useT();
  const trimmed = seed.trim();
  const seedIsEmail = trimmed.includes('@');
  const seedIsPhone =
    !seedIsEmail && digitsOf(trimmed).length >= 3 && /^[\d\s()+.-]+$/.test(trimmed);
  const [name, setName] = useState(seedIsEmail || seedIsPhone ? '' : trimmed);
  const [phone, setPhone] = useState(seedIsPhone ? trimmed : '');
  const [email, setEmail] = useState(seedIsEmail ? trimmed : '');
  const [error, setError] = useState<string | null>(null);

  const phoneId =
    phone.trim() === '' ? null : normalizeIdentity({ identity_kind: 'phone', identity: phone });
  const emailId =
    email.trim() === '' ? null : normalizeIdentity({ identity_kind: 'email', identity: email });

  /** Somebody in the book already reached by this phone or email. */
  const owner = clients.find((c) =>
    [phoneId, emailId].some(
      (id) =>
        id !== null &&
        c.identities.some(
          (i) =>
            i.identity_kind === id.identity_kind && normalizeIdentity(i)?.identity === id.identity,
        ),
    ),
  );

  const add = (): void => {
    if (name.trim() === '') return setError(t('clientPicker.needName'));
    if (phone.trim() === '' && email.trim() === '') return setError(t('clientPicker.needContact'));
    if (phone.trim() !== '' && phoneId === null) return setError(t('clientPicker.badPhone'));
    if (email.trim() !== '' && emailId === null) return setError(t('clientPicker.badEmail'));
    if (owner !== undefined) return; // The sentence and its button are already on screen.
    // Only the two kinds the client book stores; `normalizeIdentity` has
    // already turned a `wa` into a phone, and none is typed here anyway.
    const identities: WireIdentity[] = [phoneId, emailId]
      .filter((id): id is NonNullable<typeof id> => id !== null)
      .map((id) => ({
        identity_kind: id.identity_kind === 'email' ? 'email' : 'phone',
        identity: id.identity,
      }));
    onPick({
      clientId: `cl_${ulid()}`,
      name: name.trim(),
      detail: identities[0]?.identity,
      draft: { name: name.trim(), identities },
    });
  };

  return (
    <div className="client-picker__form">
      <h3>{t('clients.newClient')}</h3>
      <label className="field">
        <span>{t('common.name')}</span>
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
      </label>
      <label className="field">
        <span>{t('clients.phone')}</span>
        <input
          inputMode="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setError(null);
          }}
        />
      </label>
      <label className="field">
        <span>{t('clientPicker.email')}</span>
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
        />
      </label>
      {owner === undefined ? null : (
        <p className="client-picker__taken">
          {t('clientPicker.taken', { name: owner.display_name })}{' '}
          <button type="button" className="button" onClick={() => onPick(pickedFrom(owner))}>
            {t('clientPicker.useExisting', { name: owner.display_name })}
          </button>
        </p>
      )}
      {error === null ? null : <p className="error">{error}</p>}
      <div className="sheet__actions">
        <button type="button" className="button button--ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="button" className="button button--primary" onClick={add}>
          {t('common.add')}
        </button>
      </div>
    </div>
  );
}
